/**
 * RepBuddy - copy button with feedback (SCRUM-41)
 *
 * One button for every "copy this" affordance in the app, so they all behave
 * the same way: click, see confirmation, and be able to click again straight
 * away. Never disabled - copying the same thing twice is normal, and a button
 * that locks after one use reads as broken.
 *
 * Styling stays with the caller (each surface has its own look), so only the
 * state classes are appended here.
 */

import useCopyToClipboard from '../../hooks/useCopyToClipboard';

export default function CopyButton({
  text,
  className = '',
  doneClass = '',
  failedClass = '',
  label = 'Copy',
  doneLabel = '✓ Copied',
  failedLabel = 'Press Ctrl+C',
  title = 'Copy to clipboard',
  ariaLabel,
  resetMs = 2000,
}) {
  const { status, copy } = useCopyToClipboard(resetMs);

  const stateClass = status === 'copied' ? doneClass : status === 'failed' ? failedClass : '';
  const shown = status === 'copied' ? doneLabel : status === 'failed' ? failedLabel : label;

  // aria-live sits on the button itself. An off-screen live region would be a
  // second element in the flow, and a visually-hidden one is positioned
  // absolutely - inside this app that lands below the fold, makes the document
  // scrollable, and the chat's scrollIntoView then drags the whole page down.
  // The button's own label already changes, so announcing it here is enough.
  return (
    <button
      type="button"
      className={`${className} ${stateClass}`.trim()}
      onClick={() => copy(text)}
      title={status === 'failed' ? 'Copy blocked by your browser - select the text and press Ctrl+C' : title}
      aria-label={ariaLabel || title}
      aria-live="polite"
    >
      {shown}
    </button>
  );
}
